import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useOpticianAddMutation } from './opticianApi';
import { useEditUserMutation } from '../Patient/patientApi';
import { useDoctorListMutation } from '../Doctor/doctorApi';
import './optician.css';

const AddOptician = ({ setLoading, edit, setEdit }) => {
    const getInitialConfig = () => ({
        email: '',
        password: 'SymInfo2024',
        mobile: '',
        userType: 'OPTICIAN',
        languageId: 1,
        name: '',
        lastName: '',
        dateOfBirth: '',
        doYouWearGlasses: true,
        whenIsTheLastTimeYouWentToTheEyeDoctor: '',
        patientNameOfYourUsualEyeDoctor: '',
        patientRppsNumberOfYourUsualEyeDoctor: 0,
        phoneNumber: '',
        posid: '',
        typeOfVision: '',
        patientHappy: '',
        patientSymptoms: '',
        patientDiagnosedWith: '',
        patientHealthConditions: '',
        additionalComments: '',
        doctorRppsNumber: '',
        doctorIdOfRegistration: '',
        doctorAddressOfTheHealthCenter: '',
        isExternal: '',
        SubmitTrack: 'Web',
        infoSSN: '',
        doctorIdOpticianLink: '',
        isContactLense: 'false', // Keep as string to match select values
        posEmail: '',
    });

    const maxDateValidation = new Date().toISOString().split('T')[0];
    const { t } = useTranslation();
    const { posList } = useSelector((state) => state.pos);
    const [show, setShow] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [opticianConfig, setOpticianConfig] = useState(getInitialConfig());
    useEffect(() => {
        console.log("opticianConfig ======> ", opticianConfig)
    }, [opticianConfig]);
    const [inputStatus, setInputStatus] = useState({
        name: false,
        lastName: false,
        dateOfBirth: false,
        email: false,
        mobile: false,
        isExternal: false,
        posEmail: false,
    });

    const [doctorListMutation] = useDoctorListMutation();
    const [docList, setDocList] = useState([]);
    const [doctorsLoading, setDoctorsLoading] = useState(false);

    const [addOptician] = useOpticianAddMutation();
    const [editOptician] = useEditUserMutation();

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setOpticianConfig((prevValues) => ({
            ...prevValues,
            [name]: type === 'checkbox' ? String(checked) : value,
        }));
        setInputStatus((prev) => ({
            ...prev,
            [name]: false,
        }));
    };

    useEffect(() => {
        if (edit && Object.keys(edit).length > 0) {
            setShow(true);
            const formattedEditData = {
                ...edit,
                dateOfBirth: formatDate(edit.dateOfBirth),
                isContactLense: edit.isContactLense === true ? 'true' : 'false', // Convert boolean to string
                isOpticianExpressAccess: edit.isOpticianExpressAccess === true ? 'true' : 'false', // Convert boolean to string
                isExternal: edit.isExternal === true ? 'true' : 'false',
                posEmail: edit.posid || '', // Store the POS ID, not email
            };
            setOpticianConfig(formattedEditData);
        }
    }, [edit]);

    useEffect(() => {
        const fetchDoctors = async () => {
            if (show) {
                setDoctorsLoading(true);
                try {
                    const { data } = await doctorListMutation({
                        email: '',
                        mobile: '',
                    });
                    setDocList(data || []);
                } catch (error) {
                    console.error('Error fetching doctors:', error);
                    setDocList([]);
                } finally {
                    setDoctorsLoading(false);
                }
            }
        };
        fetchDoctors();
    }, [show, doctorListMutation]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validate required fields
        const requiredFields = {
            name: opticianConfig.name,
            lastName: opticianConfig.lastName,
            dateOfBirth: opticianConfig.dateOfBirth,
            email: opticianConfig.email,
            mobile: opticianConfig.mobile,
            isExternal: opticianConfig.isExternal !== '',
        };

        const hasEmptyFields = Object.values(requiredFields).some((value) => !value);

        // Additional validation for external opticians
        const isExternal = opticianConfig.isExternal === 'true';
        const needsPosEmail = isExternal && !opticianConfig.posEmail;

        if (hasEmptyFields || needsPosEmail) {
            setInputStatus({
                name: !opticianConfig.name,
                lastName: !opticianConfig.lastName,
                dateOfBirth: !opticianConfig.dateOfBirth,
                email: !opticianConfig.email,
                mobile: !opticianConfig.mobile,
                isExternal: opticianConfig.isExternal === '',
                posEmail: needsPosEmail,
            });
            return;
        }

        setModalLoading(true);
        try {
            // Find the selected POS to get its infoUserId
            const selectedPOS = posList.find(pos => pos.email === opticianConfig.posEmail);
            const posid = selectedPOS ? selectedPOS.infoUserId : '';

            const payload = {
                ...opticianConfig,
                phoneNumber: opticianConfig.mobile,
                isExternal: opticianConfig.isExternal === 'true',
                isContactLense: opticianConfig.isContactLense === 'true', // Convert string to boolean
                isOpticianExpressAccess: opticianConfig.isOpticianExpressAccess === 'true', // Convert string to boolean
                posid: posid, // Store the POS ID instead of email
            };

            let result;
            if (Object.keys(edit).length > 0) {
                result = await editOptician(payload);
            } else {
                result = await addOptician(payload);
            }

            if (result.data) {
                setEdit({});
                setShow(false);
                setOpticianConfig(getInitialConfig());
                setLoading(true);
            } else if (result.error) {
                console.error('API Error:', result.error);
            }
        } catch (error) {
            console.error('Error processing optician:', error);
        } finally {
            setModalLoading(false);
        }
    };

    const handleClose = () => {
        setShow(false);
        setEdit({});
        setOpticianConfig(getInitialConfig());
        setInputStatus({
            name: false,
            lastName: false,
            dateOfBirth: false,
            email: false,
            mobile: false,
            isExternal: false,
            posEmail: false,
        });
    };

    // Function to get the display email for a POS based on its ID
    const getPOSEmailFromId = (posId) => {
        if (!posId) return '';
        const pos = posList.find(item => item.infoUserId === posId);
        return pos ? pos.email : '';
    };

    return (
        <>
            <Button variant="primary" onClick={() => setShow(true)}>
                <Plus width={14} height={14} />
                {t('addOptician')}
            </Button>
            <Modal
                show={show}
                size="lg"
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Form noValidate onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>{Object.keys(edit).length > 0 ? t('editOptician') : t('addOptician')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="modal_form">
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('firstName')}*</Form.Label>
                                    <Form.Control
                                        onChange={handleChange}
                                        value={opticianConfig.name || ''}
                                        type="text"
                                        name="name"
                                        placeholder={t('inputText')}
                                        isInvalid={inputStatus.name}
                                        required
                                    />
                                    {inputStatus.name && (
                                        <Form.Control.Feedback type="invalid">
                                            {t('firstName')} {t('isRequired')}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('surName')}*</Form.Label>
                                    <Form.Control
                                        onChange={handleChange}
                                        value={opticianConfig.lastName || ''}
                                        type="text"
                                        name="lastName"
                                        placeholder={t('inputText')}
                                        isInvalid={inputStatus.lastName}
                                        required
                                    />
                                    {inputStatus.lastName && (
                                        <Form.Control.Feedback type="invalid">
                                            {t('surName')} {t('isRequired')}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('dateofBirth')}*</Form.Label>
                                    <Form.Control
                                        onChange={handleChange}
                                        max={maxDateValidation}
                                        value={opticianConfig.dateOfBirth || ''}
                                        type="date"
                                        name="dateOfBirth"
                                        placeholder={t('inputText')}
                                        isInvalid={inputStatus.dateOfBirth}
                                        required
                                    />
                                    {inputStatus.dateOfBirth && (
                                        <Form.Control.Feedback type="invalid">
                                            {t('dateofBirth')} {t('isRequired')}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('email')}*</Form.Label>
                                    <Form.Control
                                        onChange={handleChange}
                                        value={opticianConfig.email || ''}
                                        type="email"
                                        name="email"
                                        placeholder={t('inputText')}
                                        isInvalid={inputStatus.email}
                                        required
                                    />
                                    {inputStatus.email && (
                                        <Form.Control.Feedback type="invalid">
                                            {t('email')} {t('isRequired')}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('phone')}*</Form.Label>
                                    <Form.Control
                                        onChange={handleChange}
                                        value={opticianConfig.mobile || ''}
                                        type="number"
                                        name="mobile"
                                        placeholder={t('inputText')}
                                        isInvalid={inputStatus.mobile}
                                        required
                                        min="0"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                    />
                                    {inputStatus.mobile && (
                                        <Form.Control.Feedback type="invalid">
                                            {t('phone')} {t('isRequired')}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('doctorIdOpticianLink')}</Form.Label>
                                    <Form.Select
                                        name="doctorIdOpticianLink"
                                        onChange={handleChange}
                                        value={opticianConfig.doctorIdOpticianLink || ''}
                                        disabled={doctorsLoading}
                                    >
                                        <option value="">{t('selectItem')}</option>
                                        {doctorsLoading ? (
                                            <option disabled>{t('loading')}...</option>
                                        ) : docList && docList.length > 0 ? (
                                            docList.map((item, index) => (
                                                <option key={index} value={item?.email}>
                                                    {item?.email}
                                                </option>
                                            ))
                                        ) : (
                                            <option disabled>{t('noDoctorsAvailable')}</option>
                                        )}
                                    </Form.Select>
                                    {doctorsLoading && (
                                        <div className="mt-2">
                                            <Spinner animation="border" size="sm" />
                                            <span className="ms-2 text-muted">
                                                {t('loadingDoctors')}
                                            </span>
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('secondAssociateDoctor')}</Form.Label>
                                    <Form.Select
                                        name="secondaryDoctorIdOpticianLink"
                                        onChange={handleChange}
                                        value={opticianConfig?.secondaryDoctorIdOpticianLink || ''}
                                        disabled={doctorsLoading}
                                    >
                                        <option value="">{t('selectItem')}</option>
                                        {doctorsLoading ? (
                                            <option disabled>{t('loading')}...</option>
                                        ) : docList && docList.length > 0 ? (
                                            <>
                                                <option value="0">{t('None')}</option>
                                                <option value="1">{t('Any')}</option>
                                                {docList.map((item, index) => (
                                                    <option key={index} value={item?.email}>
                                                        {item?.email}
                                                    </option>
                                                ))}
                                            </>

                                        ) : (
                                            <option disabled>{t('noDoctorsAvailable')}</option>
                                        )}
                                    </Form.Select>
                                    {doctorsLoading && (
                                        <div className="mt-2">
                                            <Spinner animation="border" size="sm" />
                                            <span className="ms-2 text-muted">
                                                {t('loadingDoctors')}
                                            </span>
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("Type d'opticien")}*</Form.Label>
                                    <Form.Select
                                        name="isExternal"
                                        onChange={handleChange}
                                        value={opticianConfig.isExternal}
                                        isInvalid={inputStatus.isExternal}
                                        required
                                    >
                                        <option value="">{t('selectItem')}</option>
                                        <option value="false">{t('internalOptician')}</option>
                                        <option value="true">{t('externalOptician')}</option>
                                    </Form.Select>
                                    {inputStatus.isExternal && (
                                        <Form.Control.Feedback type="invalid">
                                            {t('opticianType')} {t('isRequired')}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            {opticianConfig.isExternal === 'true' && (
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('Courriel POS')}*</Form.Label>
                                        <Form.Select
                                            name="posEmail"
                                            onChange={handleChange}
                                            value={getPOSEmailFromId(opticianConfig.posEmail) || opticianConfig.posEmail || ''}
                                            isInvalid={inputStatus.posEmail}
                                            required
                                        >
                                            <option value="">{t('selectItem')}</option>
                                            {posList && posList.length > 0 ? (
                                                [...posList].sort((a, b) => a.email.localeCompare(b.email)).map((item, index) => (
                                                    <option key={index} value={item.email}>
                                                        {item.email}
                                                    </option>
                                                ))
                                            ) : (
                                                <option disabled>{t('noPOSAvailable')}</option>
                                            )}
                                        </Form.Select>
                                        {inputStatus.posEmail && (
                                            <Form.Control.Feedback type="invalid">
                                                {t('POS Email')} {t('isRequired')}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            )}
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('Tests lentilles')}</Form.Label>
                                    <Form.Select
                                        name="isContactLense"
                                        onChange={handleChange}
                                        value={opticianConfig.isContactLense || 'false'} // Ensure it always has a value
                                    >
                                        <option value="false">{t('no')}</option>
                                        <option value="true">{t('yes')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('expressTest')}</Form.Label>
                                    <Form.Select
                                        name="isOpticianExpressAccess"
                                        onChange={handleChange}
                                        value={opticianConfig.isOpticianExpressAccess || 'false'} // Ensure it always has a value
                                    >
                                        <option value="false">{t('no')}</option>
                                        <option value="true">{t('yes')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={modalLoading}>
                            {modalLoading && (
                                <Spinner
                                    animation="border"
                                    role="status"
                                    size="sm"
                                    className="me-1"
                                >
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            )}
                            {Object.keys(edit).length > 0 ? t('update') : t('addNewSheet')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default AddOptician;